import { IForm } from '@/modules/form/IForm';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Calendar, Edit3, FileText, MessageSquare, Plus, Trash2, X } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import Tabs from './Tabs';

interface SavedPrompt {
    id: string;
    text: string;
    draft?: boolean;
}

export default function GenerationPageDrawer() {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <div className="bg-white border-r border-stone-100 min-h-full w-80">
            <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-info to-primary-content rounded flex items-center justify-center">
                            {activeTab === 0 ? (
                                <FileText size={18} className="text-white" />
                            ) : (
                                <MessageSquare size={18} className="text-white" />
                            )}
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-stone-900">My {activeTab === 0 ? ' forms' : 'prompts'}</h2>
                            <p className="text-sm text-stone-500">Manage your {activeTab === 0 ? ' forms' : 'prompts'}</p>
                        </div>
                    </div>
                    <label htmlFor="drawer" className="btn btn-ghost btn-sm btn-circle hover:bg-stone-100 transition-colors">
                        <X size={20} className="text-stone-600" />
                    </label>
                </div>

                <Tabs tabs={['Forms', 'Prompts']} activeTab={activeTab} onChangeActiveTab={setActiveTab} width="w-full" />
            </div>

            <div className="px-6 py-4">{activeTab === 0 ? <FormList /> : <PromptList />}</div>
        </div>
    );
}

function FormList() {
    const {
        data: forms = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ['forms'],
        queryFn: async (): Promise<IForm[]> => {
            const response = await fetch('/api/forms');
            if (!response.ok) {
                throw new Error('Failed to fetch forms');
            }
            return response.json();
        },
    });

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-stone-500 text-sm">Loading forms...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-50 rounded flex items-center justify-center mx-auto mb-4">
                    <X size={24} className="text-red-500" />
                </div>
                <h3 className="text-lg font-medium text-stone-900 mb-2">Failed to load forms</h3>
                <p className="text-sm text-stone-500">{error instanceof Error ? error.message : 'An error occurred'}</p>
            </div>
        );
    }

    if (forms.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-stone-50 rounded flex items-center justify-center mx-auto mb-4">
                    <FileText size={24} className="text-stone-400" />
                </div>
                <h3 className="text-lg font-medium text-stone-900 mb-2">No forms yet</h3>
                <p className="text-sm text-stone-500 mb-6">Create your first form to get started</p>
                <label htmlFor="drawer" className="btn btn-sm btn-primary">
                    <Plus size={16} />
                    Create Form
                </label>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="pb-4 mb-4 border-b border-stone-100">
                <Link
                    href="/form-dashboard"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-stone-50 text-stone-700 rounded hover:bg-stone-100 transition-colors group"
                >
                    <span className="font-medium">View All Forms</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
            </div>
            {forms.map((form: IForm) => (
                <div
                    key={form.id}
                    className="group relative bg-white border border-stone-200 rounded p-4 hover:border-primary transition-all duration-200"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-stone-900 truncate mb-1 group-hover:text-primary-content transition-colors">
                                {form.name}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-stone-500">
                                <Calendar size={12} />
                                <span>
                                    {new Date(form.updatedAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </span>
                            </div>
                        </div>
                        <Link
                            href={`/form/${form.id}/edit`}
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-stone-400 hover:text-primary-content hover:bg-primary rounded"
                            title="Edit form"
                        >
                            <Edit3 size={16} />
                        </Link>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PromptList() {
    const [prompts, setPrompts] = useState<SavedPrompt[]>([]);

    useEffect(() => {
        const loadPrompts = () => {
            const savedPrompts = JSON.parse(localStorage.getItem('generatePagePrompts') || '[]');
            setPrompts(savedPrompts);
        };

        loadPrompts();
    }, []);

    const removePrompt = (id: string) => {
        const updatedPrompts = prompts.filter((p) => p.id !== id);
        setPrompts(updatedPrompts);
        localStorage.setItem('generatePagePrompts', JSON.stringify(updatedPrompts));
    };

    const clearAllPrompts = () => {
        setPrompts([]);
        localStorage.removeItem('generatePagePrompts');
    };

    const loadPrompt = (promptText: string) => {
        // Dispatch a custom event to load the prompt in the main textarea
        window.dispatchEvent(new CustomEvent('loadPrompt', { detail: promptText }));
    };

    if (prompts.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-stone-50 rounded flex items-center justify-center mx-auto mb-4">
                    <MessageSquare size={24} className="text-stone-400" />
                </div>
                <h3 className="text-lg font-medium text-stone-900 mb-2">No prompts yet</h3>
                <p className="text-sm text-stone-500 mb-6">Your saved prompts will appear here</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="pb-4 mb-4 border-b border-stone-100">
                <button
                    onClick={clearAllPrompts}
                    className="cursor-pointer w-full flex items-center justify-center gap-2 px-4 py-3 bg-stone-50 text-stone-700 rounded-xl hover:bg-stone-100 transition-colors group"
                >
                    <span className="font-medium">Clear All</span>
                    <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
                </button>
            </div>

            {prompts.map((prompt) => (
                <div
                    key={prompt.id}
                    className="group relative bg-white border border-stone-200 rounded p-4 hover:border-primary transition-all duration-200"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <h4 className="text-sm text-stone-900 line-clamp-2 mb-1 group-hover:text-primary-content transition-colors">
                                {prompt.text}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-stone-900">
                                {prompt.draft && (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">Draft</span>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-1 ml-3">
                            <button
                                onClick={() => loadPrompt(prompt.text)}
                                className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-stone-400 hover:text-primary-content hover:bg-primary rounded"
                                title="Load prompt"
                            >
                                <ArrowRight size={16} />
                            </button>
                            <button
                                onClick={() => removePrompt(prompt.id)}
                                className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded"
                                title="Delete prompt"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
